/**
 * Geradores de arquivo do módulo de Exportação de Dados (CSV e XLSX)
 * -----------------------------------------------------------------------------
 * Recebe linhas "planas" + colunas escolhidas e produz o arquivo já formatado
 * em português do Brasil (datas, moeda, booleanos).
 *
 * @version 1.1.0
 * RELEASE: 2026-09-07 v1.1.0 - XLSX passa a ser gerado como relatório de marca
 *          (buildBrandedXlsx) e nasce a função buildExportBlob, que devolve o
 *          arquivo pronto para download/redownload pelo cartão de resultado.
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial (extraído do DealColumnExportButton).
 */

import * as XLSX from 'xlsx';
import type { ExportColumn, ExportFormat } from './types';
import { buildBrandedXlsx, type BrandedXlsxOptions } from './xlsxReport';

/** Formata um valor conforme o tipo declarado na coluna */
export function formatExportValue(
  coluna: ExportColumn,
  valor: unknown,
  moeda = 'BRL',
): string | number {
  if (valor === null || valor === undefined || valor === '') return '';

  switch (coluna.kind) {
    case 'number':
      return Number(valor);
    case 'currency': {
      const numero = Number(valor);
      if (Number.isNaN(numero)) return '';
      try {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda }).format(numero);
      } catch {
        return numero.toFixed(2);
      }
    }
    case 'date':
      try {
        // Datas puras (YYYY-MM-DD) não devem sofrer conversão de fuso
        const iso = String(valor).slice(0, 10);
        const [a, m, d] = iso.split('-');
        return d ? `${d}/${m}/${a}` : String(valor);
      } catch {
        return String(valor);
      }
    case 'datetime':
      try {
        return new Date(String(valor)).toLocaleString('pt-BR');
      } catch {
        return String(valor);
      }
    case 'boolean':
      return valor ? 'Sim' : 'Não';
    default:
      return String(valor);
  }
}

/** Converte as linhas cruas em linhas com os rótulos das colunas escolhidas */
export function buildExportRows(
  linhas: Record<string, unknown>[],
  colunas: ExportColumn[],
): Record<string, string | number>[] {
  return linhas.map((linha) => {
    const saida: Record<string, string | number> = {};
    const moeda = typeof linha.moeda === 'string' ? (linha.moeda as string) : 'BRL';
    for (const coluna of colunas) {
      saida[coluna.label] = formatExportValue(coluna, linha[coluna.key], moeda);
    }
    return saida;
  });
}

/** Monta o conteúdo CSV (separador ';', compatível com Excel pt-BR) */
export function buildCsvContent(linhas: Record<string, string | number>[]): string {
  if (linhas.length === 0) return '';
  const cabecalhos = Object.keys(linhas[0]);
  const corpo = linhas.map((linha) =>
    cabecalhos
      .map((h) => {
        const valor = String(linha[h] ?? '');
        return /[;"\n]/.test(valor) ? `"${valor.replace(/"/g, '""')}"` : valor;
      })
      .join(';'),
  );
  return [cabecalhos.join(';'), ...corpo].join('\n');
}

/** Monta o buffer XLSX a partir das linhas já rotuladas */
export function buildXlsxBuffer(
  linhas: Record<string, string | number>[],
  nomeAba = 'Dados',
): ArrayBuffer {
  const planilha = XLSX.utils.json_to_sheet(linhas);
  const pasta = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(pasta, planilha, nomeAba.slice(0, 31));

  if (linhas.length > 0) {
    planilha['!cols'] = Object.keys(linhas[0]).map((h) => ({ wch: Math.max(h.length, 15) }));
  }

  return XLSX.write(pasta, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
}

/** Dispara o download de um Blob no navegador */
export function triggerDownload(blob: Blob, nomeArquivo: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Gera e baixa o arquivo no formato solicitado */
export function downloadExportFile(
  linhas: Record<string, string | number>[],
  formato: 'csv' | 'xls',
  nomeBase: string,
  nomeAba = 'Dados',
) {
  if (formato === 'csv') {
    const blob = new Blob(['\uFEFF' + buildCsvContent(linhas)], {
      type: 'text/csv;charset=utf-8;',
    });
    triggerDownload(blob, `${nomeBase}.csv`);
    return;
  }

  const buffer = buildXlsxBuffer(linhas, nomeAba);
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `${nomeBase}.xlsx`);
}

/**
 * Gera o arquivo final (CSV simples ou XLSX com cabeçalho de marca) e devolve
 * o Blob + nome completo, sem disparar o download. Assim o cartão de resultado
 * consegue oferecer "Baixar novamente" sem refazer a consulta.
 */
export async function buildExportBlob(opcoes: {
  formato: ExportFormat;
  nomeBase: string;
  colunas: ExportColumn[];
  /** Linhas cruas (chaves = ExportColumn.key) */
  linhas: Record<string, unknown>[];
  marca: Omit<BrandedXlsxOptions, 'colunas' | 'linhas'>;
}): Promise<{ blob: Blob; nomeArquivo: string }> {
  if (opcoes.formato === 'csv') {
    const conteudo = '\uFEFF' + buildCsvContent(buildExportRows(opcoes.linhas, opcoes.colunas));
    return {
      blob: new Blob([conteudo], { type: 'text/csv;charset=utf-8;' }),
      nomeArquivo: `${opcoes.nomeBase}.csv`,
    };
  }

  const buffer = await buildBrandedXlsx({
    ...opcoes.marca,
    colunas: opcoes.colunas,
    linhas: opcoes.linhas,
  });

  return {
    blob: new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    nomeArquivo: `${opcoes.nomeBase}.xlsx`,
  };
}
